<template>
  <div class="register-page">
    <header class="register-header">
      <a href="/" class="header-logo-link" aria-label="Lulu Rewards home">
        <img :src="logo" alt="Lulu Rewards" class="header-logo" />
      </a>
      <div class="header-meta">
        <span v-if="ownerEmail" class="header-email">{{ ownerEmail }}</span>
        <button
          type="button"
          class="header-signout"
          :disabled="signingOut"
          @click="onSignOut"
        >
          Sign out
        </button>
      </div>
    </header>

    <main class="register-main">
      <div class="register-intro">
        <h1 class="register-title">Set up your rewards programme</h1>
        <p class="register-subtitle">
          Ready to grow your crowd? Let's get you set up.
        </p>
      </div>
      <RegistrationStepper />
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import RegistrationStepper from "@/components/Organisms/RegistrationStepper.vue";
import { authStore, signOut } from "@/stores/authStore.js";
import { track } from "@/analytics.js";
import logoUrl from "@/assets/logo.png";

export default {
  name: "RegisterBusinessComponent",
  components: { RegistrationStepper },
  setup() {
    const router = useRouter();
    const ownerEmail = computed(() => authStore.user?.email || "");
    const signingOut = ref(false);
    const logo = logoUrl;

    onMounted(() => {
      track("business_registration_opened", {});
    });

    const onSignOut = async () => {
      if (signingOut.value) return;
      signingOut.value = true;
      try {
        await signOut();
        track("business_signout", { from: "register_business" });
        router.push("/business-signin");
      } finally {
        signingOut.value = false;
      }
    };

    return {
      ownerEmail,
      signingOut,
      logo,
      onSignOut,
    };
  },
};
</script>

<style scoped>
.register-page {
  min-height: 100vh;
  background: var(--lulu-bg-gradient);
  display: flex;
  flex-direction: column;
}

.register-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 2rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: saturate(180%) blur(10px);
  border-bottom: 1px solid rgba(0, 109, 62, 0.08);
  position: sticky;
  top: 0;
  z-index: 50;
}

.header-logo-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.header-logo {
  height: 50px;
  width: auto;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
}

.header-meta {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-email {
  color: var(--lulu-primary);
  background: #e6f4ee;
  font-weight: 600;
  font-size: 14px;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 6px 14px;
  border-radius: 999px;
}

@media (max-width: 540px) {
  .header-email {
    display: none;
  }
}

.header-signout {
  background: none;
  border: 1px solid var(--lulu-primary-border);
  color: var(--lulu-primary);
  font-weight: 600;
  font-size: 14px;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.header-signout:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3);
  transform: translateY(-1px);
}

.header-signout:disabled {
  opacity: 0.5;
  cursor: default;
}

.register-main {
  flex: 1 1 auto;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 32px 28px 56px;
}

.register-intro {
  margin-bottom: 24px;
  text-align: center;
}

.register-title {
  font-size: 28px;
  font-weight: 800;
  color: var(--lulu-text-strong);
  margin: 0 0 6px;
  letter-spacing: -0.6px;
}

.register-subtitle {
  font-size: 15px;
  color: var(--lulu-text-muted);
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 600px) {
  .register-header {
    padding: 14px 18px;
  }
  .register-main {
    padding: 24px 16px 40px;
  }
  .register-title {
    font-size: 24px;
  }
}
</style>
